import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaLightbulb, FaSpinner, FaBrain, FaRobot, FaStar, FaHeart } from 'react-icons/fa';
import { analyzeSongVocabulary, generateVocabularyQuestions, type VocabularyQuestion, type VocabularyAnalysis } from '../../lib/quizpractice';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: string;
  song_lyrics: string;
}

interface VocabularyQuizExerciseProps {
  song: Song;
  onComplete: (results: any) => void;
}

export default function VocabularyQuizExercise({ song, onComplete }: VocabularyQuizExerciseProps) {
  const [questions, setQuestions] = useState<VocabularyQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [startTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vocabularyAnalysis, setVocabularyAnalysis] = useState<VocabularyAnalysis | null>(null);
  const [streak, setStreak] = useState(0);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);

  useEffect(() => {
    generateAIQuestions();
  }, []);

  const generateAIQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Step 1: Analyze song lyrics to extract vocabulary
      console.log('Analyzing song vocabulary with AI...');
      const analysis = await analyzeSongVocabulary(song.song_lyrics, song.language);
      setVocabularyAnalysis(analysis);
      
      if (!analysis.words || analysis.words.length === 0) {
        throw new Error('No vocabulary words found in the song lyrics. The AI couldn\'t identify useful words to practice with.');
      }

      // Step 2: Generate questions based on the analysis
      setGeneratingQuestions(true);
      console.log('Generating vocabulary questions with AI...');
      const vocabQuestions = await generateVocabularyQuestions(analysis, 8, song.language);
      
      if (!vocabQuestions || vocabQuestions.length === 0) {
        throw new Error('Failed to generate questions. Please try again.');
      }

      setQuestions(vocabQuestions);
      console.log(`Generated ${vocabQuestions.length} AI-powered vocabulary questions`);
    } catch (error: any) {
      console.error('Error generating AI questions:', error);
      setError(error.message || 'Failed to generate vocabulary questions. Please try again.');
      
      // Fallback to basic questions if AI fails
      generateFallbackQuestions();
    } finally {
      setLoading(false);
      setGeneratingQuestions(false);
    }
  };

  const generateFallbackQuestions = () => {
    console.log('Generating fallback vocabulary questions...');
    
    // Basic Spanish vocabulary for fallback
    const basicVocabulary = [
      { word: 'amor', definition: 'Sentimiento intenso del ser humano', translation: 'love', type: 'translation' },
      { word: 'corazón', definition: 'Órgano muscular que bombea la sangre', translation: 'heart', type: 'translation' },
      { word: 'vida', definition: 'Estado de actividad de los seres orgánicos', translation: 'life', type: 'definition' },
      { word: 'tiempo', definition: 'Duración de las cosas sujetas a mudanza', translation: 'time', type: 'translation' },
      { word: 'mundo', definition: 'Conjunto de todo lo existente', translation: 'world', type: 'synonym' },
      { word: 'casa', definition: 'Edificio para habitar', translation: 'house', type: 'translation' },
      { word: 'feliz', definition: 'Que siente felicidad', translation: 'happy', type: 'antonym' },
      { word: 'triste', definition: 'Que siente tristeza', translation: 'sad', type: 'definition' }
    ];

    const fallbackQuestions: VocabularyQuestion[] = basicVocabulary.map((vocab, index) => {
      let question = '';
      let correctAnswer = '';
      let options: string[] = [];
      
      switch (vocab.type) {
        case 'translation':
          question = `¿Cuál es la traducción al inglés de "${vocab.word}"?`;
          correctAnswer = vocab.translation;
          options = [
            vocab.translation,
            vocab.word + 'ing',
            vocab.word + 'ed',
            'the ' + vocab.word
          ];
          break;
        case 'definition':
          question = `¿Cuál es la definición de "${vocab.word}"?`;
          correctAnswer = vocab.definition;
          options = [
            vocab.definition,
            `Una forma de ${vocab.word} que no existe`,
            `Lo contrario de ${vocab.word}`,
            `Un tipo de comida relacionada con ${vocab.word}`
          ];
          break;
        case 'synonym':
          question = `¿Cuál es un sinónimo de "${vocab.word}"?`;
          correctAnswer = getSynonym(vocab.word);
          options = [
            correctAnswer,
            `similar a ${vocab.word}`,
            `parecido a ${vocab.word}`,
            `como ${vocab.word} pero diferente`
          ];
          break;
        case 'antonym':
          question = `¿Cuál es un antónimo de "${vocab.word}"?`;
          correctAnswer = getAntonym(vocab.word);
          options = [
            correctAnswer,
            `opuesto a ${vocab.word}`,
            `contrario de ${vocab.word}`,
            `no ${vocab.word}`
          ];
          break;
        default:
          question = `¿Qué significa "${vocab.word}"?`;
          correctAnswer = vocab.definition;
          options = [
            vocab.definition,
            `Definición incorrecta 1`,
            `Definición incorrecta 2`,
            `Definición incorrecta 3`
          ];
      }

      return {
        word: vocab.word,
        question,
        options: shuffleArray(options),
        correctAnswer,
        explanation: `"${vocab.word}" ${vocab.type === 'translation' ? 'se traduce como' : 'significa'} "${correctAnswer}" en español.`,
        difficulty: 'medium' as const,
        type: vocab.type as any
      };
    });

    setQuestions(fallbackQuestions);
  };

  // Helper functions for fallback questions
  const getSynonym = (word: string): string => {
    const synonyms: {[key: string]: string} = {
      'amor': 'cariño',
      'corazón': 'alma',
      'vida': 'existencia',
      'tiempo': 'momento',
      'mundo': 'planeta',
      'casa': 'hogar',
      'feliz': 'contento',
      'triste': 'melancólico'
    };
    return synonyms[word] || `sinónimo de ${word}`;
  };

  const getAntonym = (word: string): string => {
    const antonyms: {[key: string]: string} = {
      'amor': 'odio',
      'feliz': 'triste',
      'triste': 'feliz',
      'grande': 'pequeño',
      'bueno': 'malo',
      'alto': 'bajo',
      'frío': 'caliente',
      'rápido': 'lento'
    };
    return antonyms[word] || `antónimo de ${word}`;
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleNextQuestion = () => {
    const question = questions[currentQuestion];
    const isCorrect = selectedAnswer === question.correctAnswer;
    
    if (isCorrect) {
      setStreak(prev => prev + 1);
    }

    const newResult = {
      questionId: question.word + currentQuestion,
      word: question.word,
      question: question.question,
      correctAnswer: question.correctAnswer,
      userAnswer: selectedAnswer,
      isCorrect,
      explanation: question.explanation,
      difficulty: question.difficulty,
      type: question.type
    };

    const newResults = [...results, newResult];
    setResults(newResults);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer('');
    } else {
      finishExercise(newResults);
    }
  };

  const finishExercise = (finalResults: any[]) => {
    const correctAnswers = finalResults.filter(r => r.isCorrect).length;
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    
    setShowResults(true);
    onComplete({
      score: correctAnswers,
      totalQuestions: questions.length,
      timeTaken,
      answers: finalResults
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'hard': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-400/20 text-green-400';
      case 'medium': return 'bg-yellow-400/20 text-yellow-400';
      case 'hard': return 'bg-red-400/20 text-red-400';
      default: return 'bg-gray-400/20 text-gray-400';
    }
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'definition': return '📖';
      case 'translation': return '🌎';
      case 'synonym': return '🔄';
      case 'antonym': return '⚖️';
      case 'usage': return '🗣️';
      default: return '❓';
    }
  };

  const renderLoading = () => (
    <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
      <div className="space-y-4 sm:space-y-6">
        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center mx-auto animate-pulse">
          <FaBrain className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
        </div>
        
        <div className="space-y-2 sm:space-y-3">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
            {generatingQuestions ? '🤖 Generando Preguntas con IA' : '🔍 Analizando Vocabulario con IA'}
          </h3>
          <p className="text-white/80 text-sm sm:text-base px-4">
            {generatingQuestions 
              ? 'Creando preguntas personalizadas basadas en el vocabulario de la canción...'
              : 'Identificando palabras y significados en las letras de la canción...'
            }
          </p>
        </div>

        <div className="flex items-center justify-center space-x-2">
          <FaSpinner className="animate-spin w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
          <span className="text-white/70 text-sm sm:text-base">
            {generatingQuestions ? 'Generando...' : 'Analizando...'}
          </span>
        </div>

        {vocabularyAnalysis && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-orange-400/30">
            <p className="text-orange-200 text-xs sm:text-sm">
              ✅ Encontradas {vocabularyAnalysis.totalWords} palabras de vocabulario en la canción
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderError = () => (
    <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
      <div className="space-y-4 sm:space-y-6">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
          <FaTimes className="w-8 h-8 sm:w-10 sm:h-10 text-red-400" />
        </div>
        
        <div className="space-y-2 sm:space-y-3">
          <h3 className="text-lg sm:text-xl font-bold text-white">Error al Generar Preguntas</h3>
          <p className="text-red-200 text-sm sm:text-base px-4">{error}</p>
        </div>

        <button
          onClick={generateAIQuestions}
          className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-lg sm:rounded-xl hover:from-orange-500 hover:to-red-500 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
        >
          Intentar de Nuevo
        </button>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = questions[currentQuestion];
    if (!question) return null;

    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Animated Progress Header */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <FaStar className="text-yellow-400 animate-pulse w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-base sm:text-lg">Pregunta {currentQuestion + 1}</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaHeart className="text-red-400 w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-sm sm:text-base">Racha: {streak}</span>
            </div>
            <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${getDifficultyBadge(question.difficulty)}`}>
              {question.difficulty.toUpperCase()}
            </div>
          </div>
          
          {/* Animated Progress Bar */}
          <div className="relative w-full h-2 sm:h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-orange-400 to-red-400 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse rounded-full"></div>
            </div>
            <div className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-xs font-bold text-white">
              {Math.round(progress)}%
            </div>
          </div>
        </div>

        {/* AI Badge */}
        <div className="flex justify-center">
          <div className="flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-orange-400/20 to-red-400/20 backdrop-blur-lg rounded-full border border-orange-400/30">
            <FaRobot className="w-3 h-3 sm:w-4 sm:h-4 text-orange-400 mr-2" />
            <span className="text-orange-200 text-xs sm:text-sm font-medium">Ejercicio Generado por IA</span>
          </div>
        </div>

        {/* Main Question Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-orange-400/30 shadow-2xl">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <span className="text-3xl sm:text-4xl mr-2 sm:mr-3">{getQuestionTypeIcon(question.type)}</span>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white animate-fade-in">
                {question.question}
              </h3>
            </div>
            
            <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-white/5 rounded-xl sm:rounded-2xl border border-white/10 mb-4 sm:mb-6">
              <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-yellow-400">{question.word}</span>
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(option)}
                className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${
                  selectedAnswer === option
                    ? 'border-orange-400 bg-orange-400/20 shadow-lg'
                    : 'border-white/20 bg-white/5 hover:border-orange-400/50 hover:bg-white/10'
                }`}
              >
                <span className="font-bold text-white text-sm sm:text-base lg:text-lg">{option}</span>
              </button>
            ))}
          </div>

          {/* Study Tip - General guidance without revealing answers */}
          <div className="text-center">
            <div className="p-3 sm:p-4 bg-blue-400/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-blue-400/20">
              <p className="text-blue-200 text-xs sm:text-sm">
                💡 Lee cuidadosamente la pregunta y piensa en el contexto de la palabra
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            onClick={handleNextQuestion}
            disabled={!selectedAnswer}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-xl sm:rounded-2xl hover:from-orange-500 hover:to-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            {currentQuestion < questions.length - 1 ? 'Siguiente Pregunta' : 'Terminar Ejercicio'}
            <FaCheck className="ml-2 sm:ml-3 w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const correctAnswers = results.filter(r => r.isCorrect).length;
    const percentage = Math.round((correctAnswers / questions.length) * 100);

    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
        {/* Celebration Header */}
        <div className="space-y-4 sm:space-y-6">
          <div className="text-6xl sm:text-7xl lg:text-8xl animate-bounce">📚</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">¡Fantástico!</h2>
          <div className="text-6xl sm:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent animate-pulse">
            {percentage}%
          </div>
          <p className="text-white/80 text-base sm:text-lg lg:text-xl px-4">
            Obtuviste {correctAnswers} de {questions.length} preguntas correctas
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-orange-400/20 rounded-full">
              <FaStar className="text-orange-400 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-white font-bold text-xs sm:text-sm">Racha Final: {streak}</span>
            </div>
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-red-400/20 rounded-full">
              <FaRobot className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-white font-bold text-xs sm:text-sm">Preguntas IA</span>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-orange-400/30">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Revisa tus Respuestas</h3>
          <div className="space-y-3 sm:space-y-4 max-h-48 sm:max-h-60 overflow-y-auto">
            {results.map((result, index) => (
              <div key={index} className="text-left bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl sm:text-2xl">{getQuestionTypeIcon(result.type)}</span>
                    <span className="font-medium text-white text-sm sm:text-base">
                      {result.word}
                    </span>
                  </div>
                  {result.isCorrect ? (
                    <FaCheck className="text-green-400 w-3 h-3 sm:w-4 sm:h-4" />
                  ) : (
                    <FaTimes className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Pregunta:</span>
                    <span className="font-medium text-white ml-2 text-xs sm:text-sm">{result.question}</span>
                  </div>
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Correcto:</span>
                    <span className="font-medium text-green-400 ml-2 text-xs sm:text-sm">{result.correctAnswer}</span>
                  </div>
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Tu respuesta:</span>
                    <span className={`font-medium ml-2 text-xs sm:text-sm ${result.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                      {result.userAnswer}
                    </span>
                  </div>
                  {result.explanation && (
                    <div className="mt-2 p-2 bg-white/5 rounded-lg">
                      <span className="text-white/60 text-xs">💡 {result.explanation}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-xl sm:rounded-2xl hover:from-orange-500 hover:to-red-500 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
        >
          Intentar de Nuevo
        </button>
      </div>
    );
  };

  if (loading) {
    return renderLoading();
  }

  if (error && questions.length === 0) {
    return renderError();
  }

  if (questions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-orange-400 border-t-transparent mx-auto mb-4"></div>
        <p className="text-white text-base sm:text-lg">Generando preguntas de vocabulario...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[500px] sm:min-h-[600px]">
      {showResults ? renderResults() : renderQuestion()}
      
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}